import axios from "axios";
import type { EnableTotp } from "../../domain/entities/EnableTotp";
import type { AllTokens } from "../../domain/entities/AllTokens";
import { ApiConfig } from "../config/apiConfig";
import { mapApiTokens } from "./tokenMappers";
import { getApiErrorMessage } from "./apiResponseUtils";
import { getClientDeviceName } from "@/features/sessions/domain/utils/deviceName";
import { clientIpHeaders } from "../../../../core/api/clientIp";

export class TotpApi {
  async resetTotp(temp_access_token: string): Promise<AllTokens | null> {
    try {
      const response = await axios.post(
        ApiConfig.RESET_TOTP,
        {},
        {
          headers: {
            Authorization: `Bearer ${temp_access_token}`,
          },
        },
      );

      const payload = response.data?.data ?? response.data;
      if (!payload || typeof payload !== "object") {
        return null;
      }

      return mapApiTokens(payload);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(
        getApiErrorMessage(error, "Failed to reset TOTP. Please try again."),
      );
    }
  }

  async enableTotp(
    access_token: string,
    totp_code: string,
  ): Promise<EnableTotp> {
    try {
      const response = await axios.post(
        ApiConfig.ENABLE_TOTP,
        {
          totp_code: totp_code,
        },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      );

      const result = response.data.data;

      const enable = {
        user_id: result.user_id,
        email: result.email,
        message: result.message,
      };

      return enable;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          "Failed to enable TOTP. Please try again.",
      );
    }
  }

  async verifyTotp(
    email: string,
    totp_code: string,
    purpose: string,
    access_token: string,
  ): Promise<AllTokens> {
    try {
      const response = await axios.post(
        ApiConfig.VERIFY_TOTP,
        {
          email: email,
          totp_code: totp_code,
          purpose: purpose,
          ...(purpose === "LOGIN" ? { device_name: getClientDeviceName() } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
            ...(await clientIpHeaders()),
          },
        },
      );

      return mapApiTokens(response.data?.data ?? response.data);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          "Failed to verify TOTP. Please try again.",
      );
    }
  }

  async sendEmailTotp(
    email: string,
    purpose: string,
    bearer_token?: string,
  ): Promise<number> {
    try {
      const response = await axios.post(
        ApiConfig.SEND_EMAIL_OTP,
        {
          email: email,
          purpose: purpose,
        },
        {
          headers: {
            Authorization: bearer_token
              ? `Bearer ${bearer_token}`
              : undefined,
          },
        },
      );

      const result = response.data.status_code;

      return result;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const data = error.response?.data;
      throw new Error(
        data?.error?.message ??
          data?.message ??
          "Failed to send TOTP. Please try again.",
      );
    }
  }

  async verifyOtp(
    email: string,
    otp_code: string,
    purpose: string,
    bearer_token?: string,
  ): Promise<AllTokens> {
    try {
      const response = await axios.post(
        ApiConfig.VERIFY_EMAIL_OTP,
        {
          email: email,
          otp_code: otp_code,
          purpose: purpose,
          ...(purpose === "LOGIN" ? { device_name: getClientDeviceName() } : {}),
        },
        {
          headers: {
            Authorization: bearer_token
              ? `Bearer ${bearer_token}`
              : undefined,
            ...(await clientIpHeaders()),
          },
        },
      );

      return mapApiTokens(response.data?.data ?? response.data);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const data = error.response?.data;
      throw new Error(
        data?.message ??
          (typeof data?.detail === "string" ? data.detail : undefined) ??
          "Failed to verify OTP. Please try again.",
      );
    }
  }
}